import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Award,
  TrendingDown,
  DollarSign,
  FileText,
  Calendar,
  Check,
  AlertTriangle,
  ArrowRight,
  Download,
  Mail,
} from "lucide-react";

const App = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Company Info
    companySize: "",
    industry: "",
    monthlySpend: "",

    // Usage Patterns
    primaryServices: [],
    workloadTypes: [],
    dataVolume: "",
    trafficPatterns: "",

    // Cost Management Practices
    reservedInstances: "",
    autoScaling: "",
    rightsizing: "",
    scheduledResources: "",
    storageOptimization: "",
    monitoringTools: [],

    // Current Challenges
    costChallenges: [],
    unutilizedResources: "",
    budgetOverruns: "",

    // Contact Info
    email: "",
    name: "",
    company: "",
  });

  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const steps = [
    { title: "Company Profile", icon: "🏢" },
    { title: "AWS Usage", icon: "☁️" },
    { title: "Cost Practices", icon: "💰" },
    { title: "Current Challenges", icon: "⚠️" },
    { title: "Get Your Report", icon: "📊" },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value],
    }));
  };

  const calculateResults = () => {
    // Simplified scoring algorithm
    let score = 100;
    let potentialSavings = 0;
    const recommendations = [];

    // Deduct points for poor practices
    if (formData.reservedInstances === "none") {
      score -= 25;
      potentialSavings +=
        parseInt(formData.monthlySpend.replace(/[^0-9]/g, "")) * 0.15;
      recommendations.push(
        "Consider Reserved Instances for 15-30% savings on predictable workloads"
      );
    }

    if (formData.autoScaling === "none") {
      score -= 20;
      potentialSavings +=
        parseInt(formData.monthlySpend.replace(/[^0-9]/g, "")) * 0.12;
      recommendations.push(
        "Implement auto-scaling to reduce over-provisioning costs by 10-15%"
      );
    }

    if (formData.rightsizing === "never") {
      score -= 20;
      potentialSavings +=
        parseInt(formData.monthlySpend.replace(/[^0-9]/g, "")) * 0.18;
      recommendations.push(
        "Right-size instances to match actual usage and save 15-25%"
      );
    }

    if (formData.scheduledResources === "never") {
      score -= 15;
      potentialSavings +=
        parseInt(formData.monthlySpend.replace(/[^0-9]/g, "")) * 0.08;
      recommendations.push(
        "Schedule non-production resources to run only when needed"
      );
    }

    if (!formData.monitoringTools.length) {
      score -= 10;
      recommendations.push(
        "Set up cost monitoring and alerts to prevent budget overruns"
      );
    }

    // Add more points for good practices
    if (formData.storageOptimization === "regularly") score += 5;
    if (formData.monitoringTools.includes("Cost Explorer")) score += 5;
    if (formData.monitoringTools.includes("Trusted Advisor")) score += 5;

    const monthlySpend =
      parseInt(formData.monthlySpend.replace(/[^0-9]/g, "")) || 5000;
    const annualSavings = Math.round(potentialSavings * 12);

    setResults({
      score: Math.max(score, 0),
      monthlySpend,
      potentialMonthlySavings: Math.round(potentialSavings),
      annualSavings,
      recommendations: recommendations.slice(0, 5),
      grade: score >= 80 ? "A" : score >= 65 ? "B" : score >= 50 ? "C" : "D",
    });
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateResults();
      setShowResults(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <Award className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Your AWS Cost Optimization Report
            </h1>
            <p className="text-gray-600">
              Discover your potential savings and optimization opportunities
            </p>
          </div>

          {/* Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Cost Efficiency Score */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-3xl font-bold mb-4 ${
                  results.grade === "A"
                    ? "bg-green-100 text-green-600"
                    : results.grade === "B"
                    ? "bg-blue-100 text-blue-600"
                    : results.grade === "C"
                    ? "bg-yellow-100 text-yellow-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {results.grade}
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Cost Efficiency Score
              </h3>
              <p className="text-3xl font-bold text-gray-900">
                {results.score}/100
              </p>
            </div>

            {/* Monthly Savings */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <TrendingDown className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Potential Monthly Savings
              </h3>
              <p className="text-3xl font-bold text-green-600">
                ${results.potentialMonthlySavings.toLocaleString()}
              </p>
            </div>

            {/* Annual Impact */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <DollarSign className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Annual Savings Potential
              </h3>
              <p className="text-3xl font-bold text-blue-600">
                ${results.annualSavings.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <AlertTriangle className="w-6 h-6 text-orange-500 mr-2" />
              Top Optimization Recommendations
            </h2>
            <div className="space-y-4">
              {results.recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="flex items-start p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-orange-600 font-semibold">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-gray-700">{rec}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              Ready to Implement These Savings?
            </h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              You could save an estimated $
              {results.annualSavings.toLocaleString()}/year. Get a free expert
              review to achieve these savings with our AWS-certified engineers.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Free Consultation
              </button>

              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center">
                <Download className="w-5 h-5 mr-2" />
                Download Full Report
              </button>
            </div>

            <p className="text-sm text-blue-200 mt-4">
              No commitment required • 30-minute expert consultation • Custom
              implementation roadmap
            </p>
          </div>

          {/* Email Capture for Report */}
          <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
            <div className="max-w-md mx-auto text-center">
              <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">
                Get Your Detailed Analysis
              </h3>
              <p className="text-gray-600 mb-6">
                Enter your email to receive the complete cost optimization
                report with detailed recommendations.
              </p>

              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="email"
                  placeholder="your.email@company.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                  Send My Detailed Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <DollarSign className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AWS Cloud Cost Optimization Assessment
          </h1>
          <p className="text-gray-600">
            Discover potential savings in your AWS environment - takes just 5
            minutes
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold ${
                    index <= currentStep
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    index + 1
                  )}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p
                    className={`text-sm font-medium ${
                      index <= currentStep ? "text-blue-600" : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 h-px mx-4 ${
                      index < currentStep ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Step 0: Company Profile */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">
                Tell us about your company
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Size
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    "1-10 employees",
                    "11-50 employees",
                    "51-200 employees",
                    "200+ employees",
                  ].map((size) => (
                    <button
                      key={size}
                      onClick={() => handleInputChange("companySize", size)}
                      className={`p-3 text-sm border rounded-lg text-center hover:border-blue-500 transition-colors ${
                        formData.companySize === size
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={formData.industry}
                  onChange={(e) =>
                    handleInputChange("industry", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select your industry</option>
                  <option value="technology">Technology</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="education">Education</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly AWS Spend
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    "Under $1,000",
                    "$1,000-$5,000",
                    "$5,000-$25,000",
                    "$25,000+",
                  ].map((spend) => (
                    <button
                      key={spend}
                      onClick={() => handleInputChange("monthlySpend", spend)}
                      className={`p-3 text-sm border rounded-lg text-center hover:border-blue-500 transition-colors ${
                        formData.monthlySpend === spend
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {spend}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: AWS Usage */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">
                Your AWS Usage Patterns
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary AWS Services (select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    "EC2",
                    "RDS",
                    "S3",
                    "Lambda",
                    "ECS/EKS",
                    "CloudFront",
                    "ELB",
                    "Route 53",
                    "Other",
                  ].map((service) => (
                    <button
                      key={service}
                      onClick={() =>
                        handleArrayToggle("primaryServices", service)
                      }
                      className={`p-3 text-sm border rounded-lg text-center hover:border-blue-500 transition-colors ${
                        formData.primaryServices.includes(service)
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workload Types
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    "Production applications",
                    "Development/Testing",
                    "Data processing/Analytics",
                    "Static websites",
                    "Backup/Archive",
                    "Machine Learning",
                  ].map((workload) => (
                    <button
                      key={workload}
                      onClick={() =>
                        handleArrayToggle("workloadTypes", workload)
                      }
                      className={`p-3 text-sm border rounded-lg text-left hover:border-blue-500 transition-colors ${
                        formData.workloadTypes.includes(workload)
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {workload}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Traffic Patterns
                </label>
                <div className="space-y-2">
                  {[
                    { value: "steady", label: "Steady/Predictable traffic" },
                    { value: "variable", label: "Variable with peak hours" },
                    { value: "seasonal", label: "Seasonal spikes" },
                    { value: "unpredictable", label: "Unpredictable/Bursty" },
                  ].map((pattern) => (
                    <label key={pattern.value} className="flex items-center">
                      <input
                        type="radio"
                        name="trafficPatterns"
                        value={pattern.value}
                        checked={formData.trafficPatterns === pattern.value}
                        onChange={(e) =>
                          handleInputChange("trafficPatterns", e.target.value)
                        }
                        className="mr-3 text-blue-600"
                      />
                      {pattern.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Cost Management Practices */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">
                Current Cost Management Practices
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Do you use Reserved Instances or Savings Plans?
                </label>
                <div className="space-y-2">
                  {[
                    {
                      value: "extensive",
                      label: "Yes, extensively (>50% of compute)",
                    },
                    { value: "some", label: "Some usage (10-50% of compute)" },
                    {
                      value: "minimal",
                      label: "Minimal usage (<10% of compute)",
                    },
                    { value: "none", label: "No, we only use On-Demand" },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="reservedInstances"
                        value={option.value}
                        checked={formData.reservedInstances === option.value}
                        onChange={(e) =>
                          handleInputChange("reservedInstances", e.target.value)
                        }
                        className="mr-3 text-blue-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto-scaling usage
                </label>
                <div className="space-y-2">
                  {[
                    {
                      value: "comprehensive",
                      label: "Comprehensive auto-scaling across services",
                    },
                    {
                      value: "basic",
                      label: "Basic auto-scaling for some services",
                    },
                    { value: "manual", label: "Mostly manual scaling" },
                    { value: "none", label: "No auto-scaling configured" },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="autoScaling"
                        value={option.value}
                        checked={formData.autoScaling === option.value}
                        onChange={(e) =>
                          handleInputChange("autoScaling", e.target.value)
                        }
                        className="mr-3 text-blue-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How often do you rightsize instances?
                </label>
                <div className="space-y-2">
                  {[
                    {
                      value: "regularly",
                      label: "Regularly (monthly reviews)",
                    },
                    { value: "quarterly", label: "Quarterly reviews" },
                    { value: "annually", label: "Annual reviews" },
                    {
                      value: "never",
                      label: "Never - instances remain as initially configured",
                    },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="rightsizing"
                        value={option.value}
                        checked={formData.rightsizing === option.value}
                        onChange={(e) =>
                          handleInputChange("rightsizing", e.target.value)
                        }
                        className="mr-3 text-blue-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cost monitoring tools used
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    "Cost Explorer",
                    "Trusted Advisor",
                    "CloudWatch",
                    "Third-party tools",
                    "Custom dashboards",
                    "No monitoring",
                  ].map((tool) => (
                    <button
                      key={tool}
                      onClick={() => handleArrayToggle("monitoringTools", tool)}
                      className={`p-3 text-sm border rounded-lg text-left hover:border-blue-500 transition-colors ${
                        formData.monitoringTools.includes(tool)
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {tool}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Current Challenges */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">
                Current Cost Challenges
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What cost challenges are you facing? (select all that apply)
                </label>
                <div className="space-y-3">
                  {[
                    "Unexpected bill increases",
                    "Difficulty tracking costs by project/department",
                    "Over-provisioned resources",
                    "Unused or idle resources",
                    "Lack of cost visibility",
                    "Budget overruns",
                    "Complex pricing model understanding",
                    "No current challenges",
                  ].map((challenge) => (
                    <button
                      key={challenge}
                      onClick={() =>
                        handleArrayToggle("costChallenges", challenge)
                      }
                      className={`w-full p-3 text-sm border rounded-lg text-left hover:border-blue-500 transition-colors ${
                        formData.costChallenges.includes(challenge)
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {challenge}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How often do you have budget overruns?
                </label>
                <div className="space-y-2">
                  {[
                    { value: "never", label: "Never - we stay within budget" },
                    {
                      value: "rarely",
                      label: "Rarely (once or twice per year)",
                    },
                    { value: "sometimes", label: "Sometimes (quarterly)" },
                    { value: "frequently", label: "Frequently (monthly)" },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="budgetOverruns"
                        value={option.value}
                        checked={formData.budgetOverruns === option.value}
                        onChange={(e) =>
                          handleInputChange("budgetOverruns", e.target.value)
                        }
                        className="mr-3 text-blue-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated percentage of unutilized resources
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {["0-10%", "10-25%", "25-50%", "50%+"].map((percentage) => (
                    <button
                      key={percentage}
                      onClick={() =>
                        handleInputChange("unutilizedResources", percentage)
                      }
                      className={`p-3 text-sm border rounded-lg text-center hover:border-blue-500 transition-colors ${
                        formData.unutilizedResources === percentage
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300"
                      }`}
                    >
                      {percentage}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Get Report */}
          {currentStep === 4 && (
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <FileText className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4">
                Ready to Generate Your Cost Optimization Report
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Based on your responses, we'll analyze your AWS usage patterns
                and identify specific opportunities to reduce costs. Your
                personalized report will include actionable recommendations and
                potential savings estimates.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
                <h3 className="font-semibold text-blue-900 mb-2">
                  Your report will include:
                </h3>
                <ul className="text-sm text-blue-700 space-y-1 text-left">
                  <li>• Cost efficiency score and grade</li>
                  <li>• Potential monthly & annual savings</li>
                  <li>• Prioritized optimization recommendations</li>
                  <li>• Industry benchmarking comparison</li>
                  <li>• Implementation roadmap</li>
                </ul>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                currentStep === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>

            <button
              onClick={nextStep}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {currentStep === steps.length - 1 ? "Generate Report" : "Next"}
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              SSL Secured
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              No Spam Guarantee
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              AWS Certified Experts
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
